(function () {
    'use strict';
    /*
     * Copyright (c) 2014 In Hand Health.
     * All rights reserved.
     */

    angular
        .module('ihh-common-web')
        .service('UserSvc', UserSvc);

    UserSvc.$inject = ['MemorySvc'];

    /**
     * Manages the user backed by localStorage.
     * Do not use <code>new</code> to create an instance. See the example below for how to use the service in a controller.
     * @ngdoc service
     * @name UserSvc
     * @memberOf ihh-common-web
     * @example
     * // Declaring the UserSvc service in a controller.
     * myModule.controller('MyCtrl', ['UserSvc', ..., function(UserSvc, ...) {
     *    ...
     * }
     */
    function UserSvc(MemorySvc) {
        var svc = this;

        svc.SESSION = 'ihh.user.session';
        svc.SELF = 'ihh.user.self';
        svc.CLINICS = 'ihh.user.clinics';
        svc.CLINIC = 'ihh.user.clinic';
        svc.ROLES = 'ihh.user.roles';
        svc.PERMISSIONS = 'ihh.user.permissions';
        svc.CLINIC_PROFESSIONALS = 'ihh.user.clinicProfessionals';
        svc.CLINIC_PROFESSIONAL = 'ihh.user.clinicProfessional';
        svc.IHH_PROFESSIONAL = 'ihh.user.ihhProfessional';

        /*
         * the role values must match those returned by the server, and need only be
         * unique per app (operations, clinic, patient)
         */
        // clinic roles
        svc.ROLE_CLINIC_ADMIN = 'admin';
        svc.ROLE_PATIENT_ADMIN = 'patient';
        svc.ROLE_THERAPIST = 'therapist';
        svc.ROLE_ASSOCIATE = 'associate';
        svc.ROLE_STAFF_COMMUNICATOR = 'staff-communicator';
        // IHH operations roles
        svc.ROLE_IHH_ADMIN = 'admin';
        svc.ROLE_IHH_CONTENT = 'content';
        svc.ROLE_IHH_ACCOUNTING = 'accounting';
        svc.ROLE_IHH_SALES = 'sales';
        svc.ROLE_LOG_READER = 'log-reader';

        svc.USERNAME = 'ihh.username';
        svc.LAST_CLINIC_ID = 'ihh.last-clinic-id';

        svc.USER = {
            SESSION: 'session',
            SELF: 'self',
            CLINICS: 'clinics',
            CLINIC: 'clinic',
            ROLES: 'roles',
            PERMISSIONS: 'permissions',
            CLINIC_PROFESSIONALS: 'clinicProfessionals',
            CLINIC_PROFESSIONAL: 'clinicProfessional',
            IHH_PROFESSIONAL: 'ihhProfessional'
        };

        var either = function (item, initial) {
            if (angular.isDefined(item) && item !== null) {
                return item;
            }
            else {
                return initial;
            }
        };

        var memOrInitial = function (CONSTANT, initial) {
            var recollection = MemorySvc.recall(MemorySvc.LOCAL, CONSTANT);
            return either(recollection, initial);
        };

        var set = function (item, CONSTANT) {
            return function (value) {
                this[item] = value;
                MemorySvc.memorize(MemorySvc.LOCAL, CONSTANT, value);
            };
        };

        var remove = function (item, CONSTANT) {
            return function () {
                delete this[item];
                MemorySvc.forget(MemorySvc.LOCAL, CONSTANT);
            };
        };

        // An empty user, used as a template for initializing or creating a logged in user.
        var defaultUser = {
            setSession: set(svc.USER.SESSION, svc.SESSION),
            removeSession: remove(svc.USER.SESSION, svc.SESSION),
            setSelf: set(svc.USER.SELF, svc.SELF),
            removeSelf: remove(svc.USER.SELF, svc.SELF),
            setClinics: set(svc.USER.CLINICS, svc.CLINICS),
            removeClinics: remove(svc.USER.CLINICS, svc.CLINICS),
            setClinic: set(svc.USER.CLINIC, svc.CLINIC),
            removeClinic: remove(svc.USER.CLINIC, svc.CLINIC),
            setRoles: set(svc.USER.ROLES, svc.ROLES),
            removeRoles: remove(svc.USER.ROLES, svc.ROLES),
            setPermissions: set(svc.USER.PERMISSIONS, svc.PERMISSIONS),
            removePermissions: remove(svc.USER.PERMISSIONS, svc.PERMISSIONS),
            setClinicProfessionals: set(svc.USER.CLINIC_PROFESSIONALS, svc.CLINIC_PROFESSIONALS),
            removeClinicProfessionals: remove(svc.USER.CLINIC_PROFESSIONALS, svc.CLINIC_PROFESSIONALS),
            setClinicProfessional: set(svc.USER.CLINIC_PROFESSIONAL, svc.CLINIC_PROFESSIONAL),
            removeClinicProfessional: remove(svc.USER.CLINIC_PROFESSIONAL, svc.CLINIC_PROFESSIONAL),
            setIhhProfessional: set(svc.USER.IHH_PROFESSIONAL, svc.IHH_PROFESSIONAL),
            removeIhhProfessional: remove(svc.USER.IHH_PROFESSIONAL, svc.IHH_PROFESSIONAL)
        };

        // The current user, after login or initialization from storage.
        var user = angular.copy(defaultUser);

        /**
         * Returns true if newRoles is not an array or oldRoles contains at least one item not in newRoles.
         * @param {Array} oldRoles the old roles array
         * @param {Array} newRoles the new roles array
         * @returns {boolean}
         */
        var lostRole = function (oldRoles, newRoles) {
            if (!angular.isArray(newRoles)) {
                return true;
            }

            if (!angular.isArray(oldRoles)) {
                return false;
            }

            for (var i = 0; i < oldRoles.length; i++) {
                if (newRoles.indexOf(oldRoles[i]) === -1) {
                    return true;
                }
            }

            return false;
        };

        /**
         * Initializes the user from storage, recovering his session etc. if available. Only call this when the web app is first
         * starting up.  Use <code>get()</code> instead to get the current user once things are rolling.
         *
         * @ngdoc function
         * @name init
         * @memberOf ihh-common-web.UserSvc
         * @returns {Object} the initialized user
         */
        svc.init = function () {
            user = angular.copy(defaultUser);

            user.setSession(memOrInitial(svc.SESSION, null));
            user.setSelf(memOrInitial(svc.SELF, null));
            user.setClinics(memOrInitial(svc.CLINICS, []));
            user.setClinic(memOrInitial(svc.CLINIC, null));
            user.setRoles(memOrInitial(svc.ROLES, []));
            user.setPermissions(memOrInitial(svc.PERMISSIONS, []));
            user.setClinicProfessionals(memOrInitial(svc.CLINIC_PROFESSIONALS, []));
            user.setClinicProfessional(memOrInitial(svc.CLINIC_PROFESSIONAL, null));
            user.setIhhProfessional(memOrInitial(svc.IHH_PROFESSIONAL, []));

            return user;
        };

        /**
         * Returns the current user that has been initialied or created. If neither of those things has happened, then the current
         * user will be empty (not logged in).
         *
         * @ngdoc function
         * @name get
         * @memberOf ihh-common-web.UserSvc
         * @returns {Object} the current user
         */
        svc.get = function () {
            return user;
        };

        /**
         * Creates a new user from a session, setting his clinics, roles, ets.
         *
         * @ngdoc function
         * @name create
         * @memberOf ihh-common-web.UserSvc
         * @param {Object} session the session to get the user from
         * @returns {Object} the create user object
         */
        svc.create = function (session) {
            user = angular.copy(defaultUser);

            var simpleSession = {
                id: session.id,
                expiration: session.expiration
            };
            if (angular.isDefined(session.sessionType)) {
                simpleSession.sessionType = session.sessionType;
            }
            user.setSession(simpleSession);
            user.setSelf(session.person);

            if (angular.isDefined(session.person.clinicProfessionals) && session.person.clinicProfessionals !== null) {
                var clinicProfessionals = session.person.clinicProfessionals;
                var usableClinics = [];
                for (var i = 0; i < clinicProfessionals.length; i++) {
                    var clinicProfessional = clinicProfessionals[i];
                    if (clinicProfessional.active === true && angular.isDefined(clinicProfessional.clinic) && clinicProfessional.clinic !== null && angular.isDefined(clinicProfessional.roles) && clinicProfessional.roles !== null && clinicProfessional.roles.length > 0) {
                        usableClinics.push(clinicProfessional.clinic);
                    }
                }
                user.setClinics(usableClinics);
                user.setClinicProfessionals(clinicProfessionals);
            }
            else if (session.person.ihhProfessional) {
                var ihh = session.person.ihhProfessional;
                if (ihh.active === true && ihh.roles) {
                    user.setRoles(ihh.roles);
                }
                user.setIhhProfessional(ihh);
            }

            return user;
        };

        /**
         * Updates the given user based on the passed session. It updates the expiration time of the session, updates roles, etc.
         * When this method returns, the current user of this service is the passed user.
         * TODO: enhance this method to handle IHH and patient roles when necessary.
         *
         * @ngdoc function
         * @name update
         * @memberOf ihh-common-web.UserSvc
         * @param {Object} theUser a user object previously returned from init() or create()
         * @param {Object} session the session object gotten from the server for this person
         * @returns {boolean} a flag indicating whether any user roles were lost
         */
        svc.update = function (theUser, session) {
            user = theUser;
            if (user && user.session && session && session.person && user.session.id === session.id) {
                if (user.session) {
                    user.session.expiration = session.expiration;
                }

                user.setSelf(session.person);

                var retVal = false;

                if (angular.isDefined(session.person.clinicProfessionals) && session.person.clinicProfessionals !== null) {
                    var clinicProfessionals = session.person.clinicProfessionals;
                    var usableClinics = [];
                    for (var i = 0; i < clinicProfessionals.length; i++) {
                        var clinicProfessional = clinicProfessionals[i];
                        if (clinicProfessional.active === true && angular.isDefined(clinicProfessional.roles) && clinicProfessional.roles !== null && clinicProfessional.roles.length > 0) {
                            usableClinics.push(clinicProfessional.clinic);

                            // It id not entirely clear why the clinic professional needs a nested copy of the person,
                            // probably for thumbnails.  What is clear is that multiple calls to this function (on idle)
                            // can cause a recursive person/prof/person/prof... nesting that is not desired and eventually
                            // blows up local storage.  Clip off the nested professionals here to stop the recursive nesting
                            clinicProfessional.person = angular.copy(session.person);
                            delete clinicProfessional.person.clinicProfessionals;

                            if (user.clinic && clinicProfessional.clinic && user.clinic.id === clinicProfessional.clinic.id) {

                                retVal = lostRole(user.roles, clinicProfessional.roles);

                                user.setClinicProfessional(clinicProfessional);
                                user.setClinic(clinicProfessional.clinic);
                                user.setRoles(clinicProfessional.roles);
                                user.setPermissions(clinicProfessional.permissions);
                            }
                        }
                    }

                    user.setClinics(usableClinics);
                    user.setClinicProfessionals(clinicProfessionals);
                }
                else if (session.person.ihhProfessional) {
                    var ihh = session.person.ihhProfessional;
                    if (ihh.active === true && ihh.roles) {

                        retVal = lostRole(user.roles, ihh.roles);

                        user.setRoles(ihh.roles);
                        user.setIhhProfessional(ihh);
                    }
                }

                return retVal;
            }
        };

        /**
         * Discards all information associated with the user.
         *
         * When this method returns, the current user of this service is the passed user.
         * TODO: enhance this method to handle IHH and patient roles when necessary.
         *
         * @ngdoc function
         * @name update
         * @memberOf ihh-common-web.UserSvc
         * @param {Object} theUser a user object previously returned from init() or create()
         * @param {Object} session the session object gotten from the server for this person
         * @returns {boolean} a flag indicating whether any user roles were lost
         */
        svc.destroy = function (theUser) {
            user = theUser;
            user.removeSession();
            user.removeSelf();
            user.removeClinics();
            user.removeClinic();
            user.removeRoles();
            user.removePermissions();
            user.removeClinicProfessionals();
            user.removeClinicProfessional();
            user.removeIhhProfessional();

            return user;
        };

        /**
         * Returns whether the user has at least one context-defined role.
         * @ngdoc method
         * @memberOf ihh-common-web.MainCtrl
         * @name hasRoles
         * @param {Object} user the user to check
         * @returns {Boolean} true if the user has roles
         */
        svc.hasRoles = function (user) {
            return angular.isDefined(user.roles) && user.roles !== null && user.roles.length > 0;
        };

        /**
         * Returns whether the user has an IHH log reader role, in those apps that support it.
         * @ngdoc method
         * @memberOf ihh-common-web.MainCtrl
         * @name isIhhLogReader
         * @param {Object} user the user to check
         * @returns {Boolean} true if the user has an IHH log reader role
         */
        svc.isIhhLogReader = function (user) {
            return svc.hasRoles(user) && user.roles.indexOf(svc.ROLE_LOG_READER) >= 0;
        };

        /**
         * Returns whether the user has an IHH admin role, in those apps that support it.
         * @ngdoc method
         * @memberOf ihh-common-web.MainCtrl
         * @name isIhhAdmin
         * @param {Object} user the user to check
         * @returns {Boolean} true if the user has an IHH admin role
         */
        svc.isIhhAdmin = function (user) {
            return svc.hasRoles(user) && user.roles.indexOf(svc.ROLE_IHH_ADMIN) >= 0;
        };

        /**
         * Returns whether the user has an IHH content manager role, in those apps that support it.
         * @ngdoc method
         * @memberOf ihh-common-web.MainCtrl
         * @name isIhhContentManager
         * @param {Object} user the user to check
         * @returns {Boolean} true if the user has an IHH content manager role
         */
        svc.isIhhContentManager = function (user) {
            return svc.hasRoles(user) && user.roles.indexOf(svc.ROLE_IHH_CONTENT) >= 0;
        };

        /**
         * Returns whether the user has an IHH accounting manager role, in those apps that support it.
         * @ngdoc method
         * @memberOf ihh-common-web.MainCtrl
         * @name isIhhAccountingManager
         * @param {Object} user the user to check
         * @returns {Boolean} true if the user has an IHH accounting manager role
         */
        svc.isIhhAccountingManager = function (user) {
            return svc.hasRoles(user) && user.roles.indexOf(svc.ROLE_IHH_ACCOUNTING) >= 0;
        };

        /**
         * Returns whether the user has an IHH sales manager, in those apps that support it.
         * @ngdoc method
         * @memberOf ihh-common-web.MainCtrl
         * @name isIhhSalesManager
         * @param {Object} user the user to check
         * @returns {Boolean} true if the user has an IHH sales manager role
         */
        svc.isIhhSalesManager = function (user) {
            return svc.hasRoles(user) && user.roles.indexOf(svc.ROLE_IHH_SALES) >= 0;
        };

        /**
         * Returns whether the user has a clinic admin role, in those apps that support it.
         * @ngdoc method
         * @memberOf ihh-common-web.MainCtrl
         * @name isClinicAdmin
         * @param {Object} user the user to check
         * @returns {Boolean} true if the user has a clinic admin role
         */
        svc.isClinicAdmin = function (user) {
            return svc.hasRoles(user) && user.roles.indexOf(svc.ROLE_CLINIC_ADMIN) >= 0;
        };

        /**
         * Returns whether the user has a clinic admin role, in those apps that support it.
         * @ngdoc method
         * @memberOf ihh-common-web.MainCtrl
         * @name isPatientAdmin
         * @param {Object} user the user to check
         * @returns {Boolean} true if the user has a patient admin role
         */
        svc.isPatientAdmin = function (user) {
            return svc.hasRoles(user) && user.roles.indexOf(svc.ROLE_PATIENT_ADMIN) >= 0;
        };

        /**
         * Returns whether the user has a clinic therapist role, in those apps that support it.
         * @ngdoc method
         * @memberOf ihh-common-web.MainCtrl
         * @name isTherapist
         * @param {Object} user the user to check
         * @returns {Boolean} true if the user has a therapist role
         */
        svc.isTherapist = function (user) {
            return svc.hasRoles(user) && user.roles.indexOf(svc.ROLE_THERAPIST) >= 0;
        };

        /**
         * Returns whether the user has a clinic associate role, in those apps that support it.
         * @ngdoc method
         * @memberOf ihh-common-web.MainCtrl
         * @name isAssociate
         * @param {Object} user the user to check
         * @returns {Boolean} true if the user has a therapist role
         */
        svc.isAssociate = function (user) {
            return svc.hasRoles(user) && user.roles.indexOf(svc.ROLE_ASSOCIATE) >= 0;
        };

        /**
         * Returns whether the user has a staff communicator role, in those apps that support it.
         * @ngdoc method
         * @memberOf ihh-common-web.MainCtrl
         * @name isStaffCommunicator
         * @param {Object} user the user to check
         * @returns {Boolean} true if the user has a staff communicator role
         */
        svc.isStaffCommunicator = function (user) {
            return svc.hasRoles(user) && user.roles.indexOf(svc.ROLE_STAFF_COMMUNICATOR) >= 0;
        };

        /**
         * Returns whether the user has a particular permission
         * @param {Object} the user object
         * @param {String} permission
         * @returns {Boolean} true if the user has the permission; false otherwise
         */
        svc.hasPermission = function (user, permission) {
            return user.permissions && user.permissions.indexOf(permission) >= 0;
        };

        /**
         * Returns whether the user is logged in.  Note that a session with a type (e.g. restricted)
         * is not considered logged in.
         * @ngdoc method
         * @name isLoggedIn
         * @memberOf ihh-common-web.MainCtrl
         */
        svc.isLoggedIn = function (user) {
            return user && user.session && !user.session.sessionType;
        };
    }
})();
